// src/components/Admin/AdminTipsGallery.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../api";

type TipCategory = "physical" | "mental" | "everyday";

type HealthTip = {
  id: number;
  category: TipCategory;
  status?: string; // pending/approved/rejected
  title?: string | null;
  body: string;
  author_name?: string | null;
  author_role?: string | null;
  image_url?: string | null;
  created_at?: string | null;
  approved_at?: string | null;
};

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");

function clean(v: any) {
  return String(v ?? "").trim();
}

function resolveUrl(url?: string | null) {
  const u = clean(url);
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  const slash = u.startsWith("/") ? "" : "/";
  return `${API_BASE}${slash}${u}`;
}

function asTips(data: any): HealthTip[] {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && Array.isArray(data.tips)) return data.tips;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

async function getWithFallback(urls: string[]) {
  let lastErr: any = null;
  for (const u of urls) {
    try {
      const r = await api.get(u);
      return r;
    } catch (e: any) {
      lastErr = e;
    }
  }
  throw lastErr;
}

async function actionWithFallback(
  urls: string[],
  methods: Array<"post" | "patch"> = ["post", "patch"],
  body?: any
) {
  let lastErr: any = null;

  for (const u of urls) {
    for (const m of methods) {
      try {
        if (m === "post") return await api.post(u, body ?? {});
        return await api.patch(u, body ?? {});
      } catch (e: any) {
        lastErr = e;
      }
    }
  }

  throw lastErr;
}

export default function AdminTipsGallery() {
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [tips, setTips] = useState<HealthTip[]>([]);

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      /**
       * ✅ Backend drift protection
       * Your console shows /tips/admin is 404.
       * Try a roster of likely endpoints.
       */
      const listPaths = [
        "/tips/admin",
        "/tips/admin/list",
        "/admin/tips",
        "/admin/tips/list",
        "/admin/health_tips",
        "/health_tips/admin",
        "/tips/moderation",
      ];

      const r = await getWithFallback(listPaths);
      const list = asTips(r.data);

      // normalize + resolve image urls
      const normalized = list.map((t) => ({
        ...t,
        status: clean(t.status || "pending").toLowerCase(),
        image_url: t.image_url ? resolveUrl(t.image_url) : null,
      }));

      setTips(normalized);
    } catch (e: any) {
      const detail = e?.response?.data?.detail ?? e?.response?.data ?? e?.message;
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
      setTips([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const qq = clean(q).toLowerCase();
    return tips.filter((t) => {
      const st = clean(t.status || "pending").toLowerCase();
      const cat = clean(t.category).toLowerCase();

      if (statusFilter !== "all" && st !== statusFilter) return false;
      if (categoryFilter !== "all" && cat !== categoryFilter) return false;

      if (qq) {
        const hay = `${t.title || ""} ${t.body || ""} ${t.author_name || ""} ${t.author_role || ""}`.toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      return true;
    });
  }, [tips, statusFilter, categoryFilter, q]);

  const approve = useCallback(
    async (id: number) => {
      setBusyId(id);
      setError("");
      try {
        const approvePaths = [
          `/tips/admin/${id}/approve`,
          `/tips/admin/approve/${id}`,
          `/admin/tips/${id}/approve`,
          `/admin/tips/approve/${id}`,
          `/health_tips/admin/${id}/approve`,
        ];

        await actionWithFallback(approvePaths, ["post", "patch"]);
        await load();
      } catch (e: any) {
        const detail = e?.response?.data?.detail ?? e?.response?.data ?? e?.message;
        setError(typeof detail === "string" ? detail : JSON.stringify(detail));
      } finally {
        setBusyId(null);
      }
    },
    [load]
  );

  const reject = useCallback(
    async (id: number) => {
      setBusyId(id);
      setError("");
      try {
        const rejectPaths = [
          `/tips/admin/${id}/reject`,
          `/tips/admin/reject/${id}`,
          `/admin/tips/${id}/reject`,
          `/admin/tips/reject/${id}`,
          `/health_tips/admin/${id}/reject`,
        ];

        await actionWithFallback(rejectPaths, ["post", "patch"]);
        await load();
      } catch (e: any) {
        const detail = e?.response?.data?.detail ?? e?.response?.data ?? e?.message;
        setError(typeof detail === "string" ? detail : JSON.stringify(detail));
      } finally {
        setBusyId(null);
      }
    },
    [load]
  );

  const btn: React.CSSProperties = {
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.16)",
    background: "rgba(255,255,255,0.92)",
    fontWeight: 900,
    cursor: "pointer",
  };

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0 }}>Tips Admin</h2>
          <div style={{ opacity: 0.75, fontSize: 13 }}>Review, approve, and reject doctor-submitted tips.</div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title/body/author..."
            style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.18)" }}
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.18)" }}
          >
            <option value="pending">pending</option>
            <option value="approved">approved</option>
            <option value="rejected">rejected</option>
            <option value="all">all</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.18)" }}
          >
            <option value="all">all categories</option>
            <option value="physical">physical</option>
            <option value="mental">mental</option>
            <option value="everyday">everyday</option>
          </select>

          <button onClick={load} disabled={loading || busyId !== null} style={btn}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {error ? (
        <div style={{ background: "#ffecec", border: "1px solid #ffb5b5", padding: 10, borderRadius: 10, marginTop: 12 }}>
          <b>Error:</b> {error}
          <div style={{ marginTop: 6, opacity: 0.85, fontSize: 12 }}>
            If this says <b>404</b>, the backend tip admin route name changed — this UI now tries multiple known paths.
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
        {filtered.length === 0 ? (
          <div style={{ opacity: 0.75 }}>{loading ? "Loading..." : "No tips match the current filters."}</div>
        ) : (
          filtered.map((t) => {
            const st = clean(t.status || "pending").toLowerCase();
            const isBusy = busyId === t.id;

            return (
              <div
                key={t.id}
                style={{
                  border: "1px solid rgba(0,0,0,0.12)",
                  borderRadius: 14,
                  padding: 12,
                  display: "grid",
                  gap: 10,
                  background: "rgba(255,255,255,0.92)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 900, fontSize: 16 }}>
                      {t.title ? t.title : <span style={{ opacity: 0.75 }}>(Untitled)</span>}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.75 }}>
                      <b>{t.category}</b> • <b>{st}</b>
                      {t.author_name ? ` • ${t.author_name}` : ""}
                      {t.author_role ? ` (${t.author_role})` : ""}
                      {t.created_at ? ` • ${t.created_at}` : ""}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <button onClick={() => approve(t.id)} disabled={isBusy || st === "approved"} style={btn}>
                      {isBusy ? "Working..." : "Approve"}
                    </button>
                    <button onClick={() => reject(t.id)} disabled={isBusy || st === "rejected"} style={btn}>
                      {isBusy ? "Working..." : "Reject"}
                    </button>
                  </div>
                </div>

                {t.image_url ? (
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <img
                      src={t.image_url}
                      alt="tip"
                      style={{
                        width: 140,
                        height: 100,
                        objectFit: "cover",
                        borderRadius: 12,
                        border: "1px solid rgba(0,0,0,0.12)",
                      }}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 260, whiteSpace: "pre-wrap", lineHeight: 1.35 }}>{t.body}</div>
                  </div>
                ) : (
                  <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.35 }}>{t.body}</div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}